import { Box, CircularProgress, Flex, Heading, Text } from "@chakra-ui/react"
import { t } from "i18next"
import { useContext, useState } from "react"
import toast from "react-hot-toast"
import { SDKContext } from "src/context/SDKContext"
import { PlansPageStyling } from "src/context/types"
import usePageStyling from "src/hooks/usePageStyling"
import axios from "src/shared/axios"
import useGridPageStyling from "../../avatars/hooks/useGridPageStyling"
import { PlansButton } from "./PlansButton"
import { PlansData } from "./PlansData"
import { StripePlan } from "./StripeSubscriptionPlans"

type CardType = "cancel" | "update" | "purchase" | "renew"

type Props = {
  stripePlan: StripePlan
  // cancel - prevents the user being billed at next billing period.
  // update - changes the user's current subscription to another plan
  // purchase - initiates a fresh new subscription
  // renew - undoes the cancel operation
  cardType: CardType
  plansData: PlansData
  fetchPlansData: () => Promise<void>
}

export const StripeSubscriptionCard = ({
  stripePlan,
  cardType,
  plansData,
  fetchPlansData
}: Props) => {
  const style: PlansPageStyling = usePageStyling("plansPageStyling")
  const branding = useGridPageStyling()

  return (
    <Flex
      direction={"column"}
      minWidth={"200px"}
      height="336px"
      px={4}
      py={3}
      borderRadius={style?.planPageButtonBorderRadius}
      bg={branding.panelBackgroundColor}
      // bg={"#222" ?? style?.planPagePanelBackgroundColor}
      // border={style?.planPagePanelBorderColor}
      transition="border ease 0.25s"
      border={`2px solid ${"#222" ?? style?.planPagePanelBackgroundColor}`}
      alignItems="center"
      gap="10px"
      _hover={{
        border: `2px solid ${style?.planPageButtonBackgroundColor}`
      }}
    >
      <Heading
        paddingBottom={2}
        size={"lg"}
        color={style?.planPagePanelSubTextColor}
      >
        {stripePlan.name}
      </Heading>
      <Text color={style?.planPagePanelSubTextColor}>
        {stripePlan.description}
      </Text>
      <Flex alignItems={"center"}>
        <Text
          fontSize={"25px"}
          fontWeight="medium"
          color={style?.planPagePanelSubTextColor}
        >
          {stripePlan.priceString}
        </Text>
        {!!stripePlan.renewalString && (
          <Text fontSize={"20px"} color={style?.planPagePanelSubTextColor}>
            /{stripePlan.renewalString}
          </Text>
        )}
      </Flex>
      <Box marginTop={"auto"} width="full">
        <CardButton
          fetchPlansData={fetchPlansData}
          plansData={plansData}
          stripePlan={stripePlan}
          cardType={cardType}
        />
      </Box>
    </Flex>
  )
}

const CardButton = ({
  cardType,
  stripePlan,
  plansData,
  fetchPlansData
}: Props) => {
  const meetingClient = useContext(SDKContext)

  const style: PlansPageStyling = usePageStyling("plansPageStyling")
  let data: { buttonText: string; onClick: () => void }

  const [loading, setLoading] = useState(false)

  const purchase = async () => {
    setLoading(true)
    const res = await axios.post(
      meetingClient?.envConfig.CREATE_STRIPE_CHECKOUT,
      {
        host: meetingClient?.envConfig.DOMAIN_HOSTNAME,
        stripeProductId: stripePlan.productId
      },
      {
        withCredentials: true
      }
    )
    if (!!res.data.redirect) {
      window.location.href = res.data.redirect
    } else {
      toast.error("Something went wrong.")
    }
    setLoading(false)
  }
  const cancel = async () => {
    setLoading(true)
    const res = await axios.post(
      meetingClient?.envConfig.CANCEL_STRIPE_SUBSCRIPTION,
      {
        host: meetingClient?.envConfig.DOMAIN_HOSTNAME,
        stripeSubscriptionId: plansData.stripeSubscriptionId
      },
      {
        withCredentials: true
      }
    )
    await delay(2000) // HACK: external webhooks are involved and we cannot know when they complete so we introduce a small delay. could be improved by using a websocket / continous polling or optimistic update
    await fetchPlansData()
    setLoading(false)
  }
  const update = async () => {
    setLoading(true)
    const res = await axios.post(
      meetingClient?.envConfig.UPDATE_STRIPE_SUBSCRIPTION,
      {
        host: meetingClient?.envConfig.DOMAIN_HOSTNAME,
        stripeSubscriptionId: plansData.stripeSubscriptionId,
        newProductId: stripePlan.productId
      },
      {
        withCredentials: true
      }
    )
    await delay(2000)
    await fetchPlansData()
    setLoading(false)
  }
  const renew = async () => {
    setLoading(true)
    const res = await axios.post(
      meetingClient?.envConfig.CANCEL_STRIPE_SUBSCRIPTION + "?undoCancel=true",
      {
        host: meetingClient?.envConfig.DOMAIN_HOSTNAME,
        stripeSubscriptionId: plansData.stripeSubscriptionId
      },
      {
        withCredentials: true
      }
    )
    await delay(2000)
    await fetchPlansData()
    setLoading(false)
  }

  switch (cardType) {
    case "purchase":
      data = {
        buttonText: t("Purchase"),
        onClick: purchase
      }
      break
    case "cancel":
      data = {
        buttonText: t("Cancel"),
        onClick: cancel
      }
      break
    case "update":
      data = {
        buttonText: t("Switch"),
        onClick: update
      }
      break
    case "renew":
      data = {
        buttonText: t("Renew"),
        onClick: renew
      }
      break
  }

  return (
    <PlansButton
      width="100%"
      disabled={loading}
      isMobile={false}
      isLandscape={false}
      onClick={data.onClick}
    >
      {loading ? (
        <CircularProgress
          isIndeterminate
          size={5}
          color={style.planPageButtonTextColor}
        />
      ) : (
        data.buttonText
      )}
    </PlansButton>
  )
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}
